import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Paper,
  TextInput,
  PasswordInput,
  Button,
  Title,
  Text,
  Stack,
  Center,
  Checkbox,
  Alert,
  PinInput,
  Group,
  Transition,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { IconAlertCircle } from '@tabler/icons-react';
import { useAuthStore, User } from '@/store/auth';
import api from '@/config/api';

interface LoginFormValues {
  username: string;
  password: string;
  remember: boolean;
}

export default function Login() {
  const navigate = useNavigate();
  const login = useAuthStore((s) => s.login);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [mfaRequired, setMfaRequired] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [tempToken, setTempToken] = useState('');

  const form = useForm<LoginFormValues>({
    initialValues: {
      username: '',
      password: '',
      remember: false,
    },
    validate: {
      username: (v) => (v.trim().length === 0 ? 'Username is required' : null),
      password: (v) => (v.length === 0 ? 'Password is required' : null),
    },
  });

  const handleLogin = async (values: LoginFormValues) => {
    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/auth/login', {
        username: values.username,
        password: values.password,
      });

      if (data.mfa_required) {
        setMfaRequired(true);
        setTempToken(data.temp_token);
      } else {
        const user: User = data.user;
        login(user, data.access_token);
        navigate('/dashboard');
      }
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleMfaVerify = async () => {
    setLoading(true);
    setError('');

    try {
      const { data } = await api.post('/auth/mfa/verify', {
        temp_token: tempToken,
        code: mfaCode,
      });

      const user: User = data.user;
      login(user, data.access_token);
      navigate('/dashboard');
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { detail?: string } } };
      setError(axiosErr.response?.data?.detail || 'Invalid MFA code.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center
      style={{
        minHeight: '100vh',
        background:
          'linear-gradient(135deg, #0a1628 0%, #1a2744 50%, #0d1f3c 100%)',
      }}
    >
      <Paper shadow="xl" p={40} radius="md" w={420}>
        <Stack align="center" mb="lg">
          <img src="/logo.png" alt="Standard Produce Co." style={{ height: 120 }} />
        </Stack>

        {error && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            mb="md"
            variant="light"
          >
            {error}
          </Alert>
        )}

        <Transition
          mounted={!mfaRequired}
          transition="fade"
          duration={200}
        >
          {(styles) => (
            <form onSubmit={form.onSubmit(handleLogin)} style={styles}>
              <Stack gap="md">
                <TextInput
                  label="Username"
                  placeholder="Enter your username"
                  autoComplete="username"
                  {...form.getInputProps('username')}
                  size="md"
                />
                <PasswordInput
                  label="Password"
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  {...form.getInputProps('password')}
                  size="md"
                />
                <Group justify="space-between">
                  <Checkbox
                    label="Remember me"
                    {...form.getInputProps('remember', { type: 'checkbox' })}
                    size="sm"
                  />
                  <Text
                    size="xs"
                    c="brand"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate('/forgot-password')}
                  >
                    Forgot password?
                  </Text>
                </Group>
                <Button
                  type="submit"
                  fullWidth
                  size="md"
                  loading={loading}
                >
                  Sign In
                </Button>
              </Stack>
            </form>
          )}
        </Transition>

        <Transition
          mounted={mfaRequired}
          transition="fade"
          duration={200}
        >
          {(styles) => (
            <Stack gap="md" style={styles}>
              <Text size="sm" ta="center" c="dimmed">
                Enter the 6-digit code from your authenticator app
              </Text>
              <Center>
                <PinInput
                  length={6}
                  type="number"
                  value={mfaCode}
                  onChange={setMfaCode}
                  size="md"
                  oneTimeCode
                />
              </Center>
              <Button
                fullWidth
                size="md"
                loading={loading}
                onClick={handleMfaVerify}
                disabled={mfaCode.length !== 6}
              >
                Verify
              </Button>
              <Button
                variant="subtle"
                size="sm"
                onClick={() => {
                  setMfaRequired(false);
                  setMfaCode('');
                  setTempToken('');
                }}
              >
                Back to login
              </Button>
            </Stack>
          )}
        </Transition>

        <Text ta="center" size="xs" c="dimmed" mt="xl">
          Standard Produce ERP v1.3.0
        </Text>
      </Paper>
    </Center>
  );
}
